/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* Parent Class for Chart Objects to be displayed above tables
*/
import * as Highcharts from 'highcharts';

import { AbstractObject, uuid } from "@src/app/model/abstract-object";
import { NumberField } from "@src/app/shared/field/NumberField";
import { PicklistField } from "@src/app/shared/field/PicklistField";
import { TrendField } from '@src/app/shared/field/TrendField';
import { AppFilterNavigator, ChartSort } from '@src/app/shared/chart/chart-analysis';
import { MyInjector } from '@src/app/app.module';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
// pct is custom...
interface SeriesData { name: string, y?: number, pct?: number, customdata?: number | string, type?: string, data?: number[] }

/** What Highcharts binds `this` to inside a pointFormatter - percentage is its own, pct is ours */
export interface ChartPoint { y: number, pct?: number, percentage?: number, series: { name: string } }

interface AppChartOptions {
    data: AbstractObject[],
    divId: string,
    picklist?: PicklistField,
    numberField?: NumberField,
    trendField?: TrendField,
    refresher: AppFilterNavigator,
    excludeZero?: boolean,
    suppressSubTitle?: boolean,
    showOnly?: number,
    sortBy?: ChartSort
}
type AnalysisMap = Map<uuid, { name: string; total: number }>

/** Numeric ids sort as numbers so 9 comes before 10, everything else as text */
export const compareKeys = (a: number | string, b: number | string) => {
    if (!isNaN(+a) && !isNaN(+b)) {
        return +a - +b;
    }
    return String(a).localeCompare(String(b));
};

export abstract class AbstractChart {

    protected data: AbstractObject[] = [];
    protected series: SeriesData[];
    readonly othersEntryId = 'others-id';
    height: number;
    width: number;

    protected picklist: PicklistField;
    protected numberField: NumberField;
    protected trendField: TrendField;

    chartBackgroundColor = 'white'; //'rgba(240, 239, 238, 1)';
    chartBorderRadius = 10;

    refresher: AppFilterNavigator

    private excludeZero = false;
    private suppressSubTitle = false;
    private sortBy: ChartSort;

    /** Each chart type's own sensible cap; showOnlyOverride is what the ChartParms asked for */
    protected showOnly = 0;
    private showOnlyOverride: number;

    protected abstract getTooltip();

    protected abstract createChartOptions();

    getChartTitle() {
        return { text: this.picklist.label, style: { fontSize: '14px', fontWeight: 'bold' } };
    }

    getSeriesName() {
        return this.numberField ? this.numberField.label : 'Count';
    }

    /**
     * Highcharts would print the raw float, floating point noise and all. The metric field already
     * knows whether it is currency, a percentage or a plain count, so let it do the formatting.
     */
    protected formatMetric(value: number) {
        return this.numberField ? this.numberField.formatValue(value) : value;
    }

    getSubTitle() {
        //return { text: this.getSeriesName(), style: {fontSize: '12px'} }
        return { text: this.suppressSubTitle ? '' : this.getSeriesName() };
    }

    filterData($event) {
        const value = $event.point.customdata;
        if (value === this.othersEntryId) {
            const cds = MyInjector.instance.get(ConfirmDialogService);
            cds.alert($localize `Sorry Cannot Filter`, $localize `We cannot filter to show other entries, sorry`);
        } else {
            this.picklist.filter.value = value ? value : '';

            this.picklist.filter.active = true;
            this.refresher();
        }
    }

    setupData(options: AppChartOptions) {

        this.picklist = options.picklist;
        this.data = options.data;
        this.numberField = options.numberField;
        this.trendField = options.trendField;
        this.refresher = options.refresher;
        this.excludeZero = options.excludeZero;
        this.suppressSubTitle = options.suppressSubTitle;
        this.showOnlyOverride = options.showOnly;
        this.sortBy = options.sortBy;

        this.analyseData();

        Highcharts.chart(options.divId, (this.createChartOptions() as Highcharts.Options));
    }

    protected analyseData() {
        let mapper: AnalysisMap = new Map();
        let grandTotal = this.numberField ? 0 : this.data.length;
        const type = this.picklist.cellOpts.heading || this.picklist.label || this.picklist.name;

        this.series = [];

        this.data.map(o => {
            const idValue = this.picklist.getValue(o);
            let displayValue = 'Not Entered';
            if (!mapper.has(idValue)) {
                if (idValue) {
                    displayValue = this.picklist.getDisplayValueFromId(idValue);
                }
                mapper.set(idValue, { name: displayValue, total: 0 })
            }

            const oMap = mapper.get(idValue);
            if (this.numberField) {
                const t = this.numberField.getValue(o);
                oMap.total = oMap.total + t;
                grandTotal += t;
            } else {
                oMap.total = oMap.total + (this.numberField ? this.numberField.getValue(o) : 1);
            }
        });
        if (this.getShowOnly() && (mapper.size > this.getShowOnly() )) {
            mapper = this.getTopWithOthers(mapper);
        }
        for (const k of mapper.keys()) {
            const oMap = mapper.get(k);
            // Pct this.series.push({name: oMap.name, y: (oMap.total / total * 100), id: k, type})
            if (oMap.total !== 0 || !this.excludeZero) {
                this.series.push({ name: oMap.name, y: oMap.total, pct: (oMap.total / grandTotal * 100), customdata: k, type });
            }
        }
        this.sortSeries();
    }

    /** A chart can raise its own cap through the ChartParms, or drop it entirely with 0 */
    private getShowOnly() {
        return this.showOnlyOverride ?? this.showOnly;
    }

    /**
     * Entries come out in the order the rows arrived, which means nothing for an ordinal
     * picklist - a month chart wants its bars in month order whatever the server sent.
     * "Total of others" is a bucket rather than an entry, so it stays on the end.
     */
    private sortSeries() {
        if (!this.sortBy) {
            return;
        }
        this.series.sort((a, b) => {
            if (a.customdata === this.othersEntryId) { return 1; }
            if (b.customdata === this.othersEntryId) { return -1; }
            return this.sortBy === 'value' ? b.y - a.y : compareKeys(a.customdata, b.customdata);
        });
    }

    getTopWithOthers(originalMap: AnalysisMap): AnalysisMap {
        if (originalMap.size <= this.getShowOnly()) {
          return new Map(originalMap); // return a copy of the original if < 5 entries
        }
      
        // Sort entries by total descending
        const sortedEntries = Array.from(originalMap.entries()).sort(
          (a, b) => b[1].total - a[1].total
        );
      
        const result: AnalysisMap = new Map();

        let otherTotal = 0;
      
        sortedEntries.forEach((entry, index) => {
          const [uuid, data] = entry;
          if (index < this.showOnly) {
            result.set(uuid, data);
          } else {
            otherTotal += data.total;
          }
        });
      
        if (otherTotal > 0) {
          result.set(this.othersEntryId, { name: "Total of others", total: otherTotal });
        }
      
        return result;
      }    

}

